import {
  Button,
  FormControlLabel,
  Link,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { AxiosError } from 'axios';
import React, {
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import AuthCode from 'react-auth-code-input';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';

import { authTwoFAApi, usersApi } from 'api';
import {
  CloseDialogResult,
  CopyText,
  DataWrapper,
  Dialog,
  HelpTooltip,
} from 'components';
import { IP_REGEX } from 'constants/common.constants';
import { QueryKey, SecureConfirmType } from 'enums';
import { useMeta, useMutation } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { User } from 'types';
import { authUtils, formatUtils, validationUtils } from 'utils';

const TwoFASecretInfo: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.settings.security.two_fa',
  });
  const queryClient = useQueryClient();
  const queryResult = useQuery(QueryKey.TwoFA, authTwoFAApi.get);
  const { mutate: resetSecret, isLoading: resetSecretLoading } = useMutation(
    authTwoFAApi.reset,
    {
      requireSecureConfirm: true,
      secureConfirmTypes: [SecureConfirmType.LoginPassword],
      onSuccess: () => {
        queryClient.invalidateQueries(QueryKey.TwoFA);
        queryClient.invalidateQueries(QueryKey.Profile);
      },
    },
  );

  return (
    <DataWrapper queryResult={queryResult}>
      <div>
        <div className="tw-font-bold">{t('secret')}</div>
        <CopyText
          text={queryResult.data ? queryResult.data.secret : ''}
          truncateLength={25}
        />
        <div className="tw-font-bold tw-mt-8">{t('qr')}</div>
        <div>
          <img src={queryResult.data?.qr} alt="qr" className="tw-border" />
        </div>
        <div className="tw-my-4">
          <Button
            color="error"
            variant="outlined"
            onClick={() => resetSecret({})}
            disabled={resetSecretLoading}
          >
            {t('buttons.reset')}
          </Button>
        </div>
      </div>
    </DataWrapper>
  );
};

type IpWhitelistSectionProps = {
  user?: User;
};

const IpWhitelistSection: React.FC<IpWhitelistSectionProps> = ({ user }) => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.settings.security.ip_whitelist',
  });
  const { t: tCommon } = useTranslation(TranslationNamespace.Common);
  const queryClient = useQueryClient();

  const twoFAEnabled = !!user?.twoFAEnabled;
  const currentIp = user?.currentIp;
  const initialList = useMemo(
    () => formatUtils.formatList(user?.ipWhitelist),
    [user?.ipWhitelist],
  );

  const [value, setValue] = useState(initialList);
  const [serverError, setServerError] = useState<string | undefined>(undefined);
  useEffect(() => setValue(initialList), [initialList]);

  const parsed = useMemo(() => formatUtils.parseList(value), [value]);
  const invalid = parsed.some((ip) => !IP_REGEX.test(ip));
  const changed = value !== initialList;

  const { mutate: save, isLoading } = useMutation<
    User,
    AxiosError,
    { ipWhitelist: string[] }
  >(usersApi.updateIpWhitelist, {
    requireSecureConfirm: true,
    secureConfirmTypes: [SecureConfirmType.TwoFA],
    onSuccess: () => {
      setServerError(undefined);
      queryClient.invalidateQueries(QueryKey.Profile);
    },
    onError: (error: AxiosError) => {
      const errors = validationUtils.getFormErrors(error);
      const fieldError = errors.ipWhitelist;
      const message = Array.isArray(fieldError)
        ? fieldError.join(', ')
        : typeof fieldError === 'string'
        ? fieldError
        : tCommon('errors.ip');
      setServerError(message);
    },
  });

  const handleAddCurrent = useCallback(() => {
    if (!currentIp) {
      return;
    }
    const next = formatUtils.parseList(value);
    if (next.includes(currentIp)) {
      return;
    }
    next.push(currentIp);
    setValue(formatUtils.formatList(next));
  }, [value, currentIp]);

  const handleSave = useCallback(() => {
    save({ ipWhitelist: parsed });
  }, [parsed, save]);

  return (
    <div className="tw-mb-8 tw-max-w-xs">
      <div className="tw-text-base tw-mb-2 tw-flex tw-items-center">
        {t('title')}
        <HelpTooltip title={t('tooltip')} />
      </div>
      <TextField
        variant="standard"
        fullWidth
        multiline
        disabled={!twoFAEnabled}
        value={value}
        onChange={(e) => {
          setValue(e.target.value);
          setServerError(undefined);
        }}
        error={invalid || !!serverError}
        helperText={
          !twoFAEnabled
            ? t('two_fa_required')
            : serverError || (invalid ? tCommon('errors.ip') : t('hint'))
        }
      />
      {twoFAEnabled && currentIp && (
        <Typography
          variant="caption"
          color="textSecondary"
          sx={{ display: 'block', mt: 0.5 }}
        >
          {t('current_ip', { ip: currentIp })}
          {!parsed.includes(currentIp) && (
            <Fragment>
              {' '}
              <Link
                component="button"
                type="button"
                variant="caption"
                onClick={handleAddCurrent}
              >
                {t('current_ip_add')}
              </Link>
            </Fragment>
          )}
        </Typography>
      )}
      {twoFAEnabled && (
        <div className="tw-mt-4">
          <Button
            type="button"
            variant="outlined"
            disabled={!changed || invalid || isLoading}
            onClick={handleSave}
          >
            {tCommon('buttons.save')}
          </Button>
        </div>
      )}
    </div>
  );
};

export const Security: React.FC = () => {
  const { t } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.settings.security',
  });

  const { required2FARoles, isTokenIpValidationEnabled } = useMeta();
  const isRequired2FA = useMemo(
    () => required2FARoles.includes(authUtils.getUserRole()),
    [required2FARoles],
  );

  const [authenticateDialogOpen, setAuthenticateDialogOpen] = useState(false);
  const [authCode, setAuthCode] = useState<string>('');

  const queryClient = useQueryClient();
  const queryResult = useQuery(QueryKey.Profile, usersApi.getProfile);
  const twoFAEnabled = useMemo(
    () => queryResult?.data?.twoFAEnabled,
    [queryResult],
  );

  const { mutate: disable } = useMutation(authTwoFAApi.disable, {
    requireSecureConfirm: true,
    secureConfirmTypes: [SecureConfirmType.LoginPassword],
    onSuccess: () => {
      queryClient.invalidateQueries(QueryKey.Profile);
    },
  });
  const { mutate: enable } = useMutation(authTwoFAApi.enable);

  const handleTwoFAEnabledToggle = useCallback(() => {
    if (twoFAEnabled) {
      disable({});
    } else {
      setAuthenticateDialogOpen(true);
    }
  }, [disable, twoFAEnabled]);

  const handleAuthenticateDialogClose = useCallback(
    ({ ok }: CloseDialogResult) => {
      if (ok) {
        enable(authCode, {
          onSuccess: () => {
            setAuthenticateDialogOpen(false);
            queryClient.invalidateQueries(QueryKey.Profile);
          },
        });
      } else {
        setAuthenticateDialogOpen(false);
      }
    },
    [authCode, enable, queryClient],
  );

  return (
    <div className="tw-my-4">
      <div className="tw-text-xl tw-mb-4">{t('title')}</div>
      <DataWrapper queryResult={queryResult}>
        <Fragment>
          <div className="tw-mb-8">
            <div className="tw-text-base tw-mb-2">{t('two_fa.title')}</div>
            <FormControlLabel
              className="tw-mb-2"
              control={
                <Switch
                  checked={twoFAEnabled}
                  onChange={handleTwoFAEnabledToggle}
                  disabled={isRequired2FA && twoFAEnabled}
                />
              }
              label={twoFAEnabled ? t('two_fa.enabled') : t('two_fa.disabled')}
            />
            {isRequired2FA && (
              <div className="tw-text-sm tw-text-gray-500 tw-mb-6">
                {t('two_fa.required_for_role')}
              </div>
            )}
            <div className="tw-mb-4">
              <div className="tw-font-bold">{t('two_fa.app_download')}</div>
              <span className="tw-mr-4">
                <Link
                  href="https://apps.apple.com/us/app/google-authenticator/id388497605"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {t('two_fa.app_store')}
                </Link>
              </span>
              <Link
                href="https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2"
                target="_blank"
                rel="noopener noreferrer"
              >
                {t('two_fa.play_market')}
              </Link>
            </div>
            <TwoFASecretInfo />
          </div>
          {isTokenIpValidationEnabled && (
            <IpWhitelistSection user={queryResult.data} />
          )}
        </Fragment>
      </DataWrapper>
      <Dialog
        title={t('two_fa.enable_dialog.title')}
        open={authenticateDialogOpen}
        okDisabled={authCode.length !== 6}
        onClose={handleAuthenticateDialogClose}
      >
        <div className="tw-text-center">
          {t('two_fa.enable_dialog.code_2fa')}
        </div>
        <AuthCode
          containerClassName="tw-px-4 tw-text-center"
          inputClassName="tw-w-10 tw-h-10 tw-m-1 tw-border tw-border-2 tw-text-center tw-text-black"
          allowedCharacters="numeric"
          autoFocus
          onChange={setAuthCode}
        />
      </Dialog>
    </div>
  );
};
